import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { serverURL } from '../constants';
import axios from 'axios';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import {
    IoBulb, IoBookmark, IoTime, IoTrendingUp,
    IoRefresh, IoSearch, IoSparkles, IoSchool,
} from 'react-icons/io5';
import { FaMagic, FaFolderOpen } from 'react-icons/fa';
import Header from '../components/header';
import IdeaCard from '../components/courseIdeas/IdeaCard';
import LearningAdvisor from '../components/courseIdeas/LearningAdvisor';
import logger from '../utils/logger';
import { notifyFromApiError } from '../utils/notify';
import { isInsideRoleShell } from '../shared/ui/roleShell';
import { useShellNavigate } from '../shared/ui/useShellNavigate';
import { PageHeader } from '../shared/ui/AppShell';

const LOCAL_RECENT_KEY = 'courseIdeas_recent';
const SKILL_LEVELS = ['all', 'beginner', 'intermediate', 'advanced'];

const CourseIdeas = () => {
    const { t, i18n } = useTranslation();
    const navigate = useShellNavigate();
    const location = useLocation();
    const embedded = isInsideRoleShell();

    const [userId, setUserId] = useState(null);
    const [topic, setTopic] = useState('');
    const [skillLevel, setSkillLevel] = useState('all');
    const [isGenerating, setIsGenerating] = useState(false);
    const [generatedIdeas, setGeneratedIdeas] = useState([]);
    const [savedIdeas, setSavedIdeas] = useState([]);
    const [recentIdeas, setRecentIdeas] = useState([]);
    const [trendingIdeas, setTrendingIdeas] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [viewMode, setViewMode] = useState('generate');
    const [generateMode, setGenerateMode] = useState('quick');
    const [showAdvisor, setShowAdvisor] = useState(false);
    const [learningProfile, setLearningProfile] = useState(null);
    const [loading, setLoading] = useState({ recent: true, trending: true, saved: false, suggestions: true });
    const [searchTerm, setSearchTerm] = useState('');
    const [filterDifficulty, setFilterDifficulty] = useState('all');
    const [sortBy, setSortBy] = useState('relevance');
    const [lastSource, setLastSource] = useState(null);
    const [projectCount, setProjectCount] = useState(0);
    const viewedIdeasRef = useRef(new Set());

    useEffect(() => {
        const uid = sessionStorage.getItem('uid');
        setUserId(uid || null);
    }, []);

    useEffect(() => {
        if (location.state?.prefillAnswers) {
            setLearningProfile(location.state.prefillAnswers);
            setShowAdvisor(true);
            setGenerateMode('advisor');
            toast.info(t('courseIdeas.toast.learningProfileRestored'));
        }
    }, [location.state, t]);

    const loadLocalRecent = useCallback(() => {
        try {
            const raw = localStorage.getItem(LOCAL_RECENT_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    }, []);

    const saveLocalRecent = useCallback((ideas, searchTopic) => {
        try {
            const existing = loadLocalRecent();
            const stamped = ideas.map((idea) => ({ ...idea, sourceTopic: searchTopic, savedAt: Date.now() }));
            const merged = [...stamped, ...existing].slice(0, 20);
            localStorage.setItem(LOCAL_RECENT_KEY, JSON.stringify(merged));
        } catch (e) {
            logger.warn('Failed to save local recent:', e.message);
        }
    }, [loadLocalRecent]);

    const loadSavedIdeas = useCallback(async (uid) => {
        if (!uid) return;
        try {
            setLoading((l) => ({ ...l, saved: true }));
            const res = await axios.get(`${serverURL}/api/saved-course-ideas/${uid}`);
            if (res.data.success) setSavedIdeas(res.data.ideas || []);
        } catch (e) {
            logger.error('Error loading saved ideas:', e);
        } finally {
            setLoading((l) => ({ ...l, saved: false }));
        }
    }, []);

    const loadRecentIdeas = useCallback(async (uid) => {
        try {
            setLoading((l) => ({ ...l, recent: true }));
            const params = uid ? { userId: uid } : {};
            const res = await axios.get(`${serverURL}/api/recent-course-ideas`, { params, timeout: 15000 });
            const serverIdeas = res.data.success ? (res.data.ideas || []) : [];
            const localIdeas = loadLocalRecent();
            const seen = new Set();
            const merged = [];
            for (const idea of [...serverIdeas, ...localIdeas]) {
                const key = idea._id || idea.id || idea.title;
                if (!seen.has(key)) {
                    seen.add(key);
                    merged.push(idea);
                }
            }
            setRecentIdeas(merged.slice(0, 12));
        } catch (e) {
            setRecentIdeas(loadLocalRecent().slice(0, 12));
        } finally {
            setLoading((l) => ({ ...l, recent: false }));
        }
    }, [loadLocalRecent]);

    const loadTrendingIdeas = useCallback(async () => {
        try {
            setLoading((l) => ({ ...l, trending: true }));
            const res = await axios.get(`${serverURL}/api/trending-course-ideas`, { timeout: 15000 });
            if (res.data.success) setTrendingIdeas(res.data.ideas || []);
        } catch (e) {
            setTrendingIdeas([]);
        } finally {
            setLoading((l) => ({ ...l, trending: false }));
        }
    }, []);

    const loadSuggestions = useCallback(async (uid) => {
        try {
            setLoading((l) => ({ ...l, suggestions: true }));
            const params = uid ? { userId: uid } : {};
            const res = await axios.get(`${serverURL}/api/course-ideas/suggestions`, { params, timeout: 10000 });
            if (res.data.success) setSuggestions(res.data.suggestions || []);
        } catch (e) {
            setSuggestions([]);
        } finally {
            setLoading((l) => ({ ...l, suggestions: false }));
        }
    }, []);

    useEffect(() => {
        loadSuggestions(userId);
        loadTrendingIdeas();
        if (userId) {
            loadSavedIdeas(userId);
            loadRecentIdeas(userId);
            axios.get(`${serverURL}/api/projects`, { timeout: 15000 })
                .then((res) => {
                    const list = res.data?.data ?? res.data ?? [];
                    setProjectCount(Array.isArray(list) ? list.length : 0);
                })
                .catch(() => setProjectCount(0));
        } else {
            setRecentIdeas(loadLocalRecent().slice(0, 12));
            setLoading((l) => ({ ...l, recent: false }));
            setProjectCount(0);
        }
    }, [userId, loadSuggestions, loadTrendingIdeas, loadSavedIdeas, loadRecentIdeas, loadLocalRecent]);

    useEffect(() => {
        if (viewMode === 'recent') {
            if (userId) loadRecentIdeas(userId);
            else setRecentIdeas(loadLocalRecent().slice(0, 12));
        }
        if (viewMode === 'trending') loadTrendingIdeas();
        if (viewMode === 'saved' && userId) loadSavedIdeas(userId);
        if (viewMode === 'trending') setSortBy('trending');
        else if (viewMode === 'generate') setSortBy('relevance');
    }, [viewMode, userId, loadRecentIdeas, loadTrendingIdeas, loadSavedIdeas, loadLocalRecent]);

    const trackInteraction = useCallback(async (ideaId, action) => {
        if (!ideaId || String(ideaId).startsWith('curated_')) return;
        if (action === 'view') {
            const key = String(ideaId);
            if (viewedIdeasRef.current.has(key)) return;
            viewedIdeasRef.current.add(key);
        }
        try {
            await axios.post(`${serverURL}/api/course-idea-interaction`, { ideaId, action });
        } catch {
            // non-blocking
        }
    }, []);

    const generateIdeas = async (opts = {}) => {
        const searchTopic = (opts.topic || topic).trim();
        const profile = opts.learningProfile || learningProfile;

        if (!searchTopic && !profile?.specificNeeds) {
            toast.error(t('courseIdeas.validation.enterTopic'));
            return;
        }

        const effectiveTopic = searchTopic || profile?.specificNeeds;

        try {
            setIsGenerating(true);
            setGeneratedIdeas([]);
            toast.info(t('courseIdeas.toast.generatingIdeas'));

            const res = await axios.post(`${serverURL}/api/generate-intelligent-course-ideas`, {
                topic: effectiveTopic,
                language: i18n.language || 'en',
                skillLevel: skillLevel !== 'all' ? skillLevel : undefined,
                learningProfile: profile,
                userId: userId || undefined,
            }, { timeout: 90000 });

            if (res.data.success) {
                const ideas = res.data.ideas || [];
                setGeneratedIdeas(ideas);
                setLastSource(res.data.source);
                saveLocalRecent(ideas, effectiveTopic);
                loadRecentIdeas(userId);

                if (res.data.source === 'ai') {
                    toast.success(t('courseIdeas.toast.ideasReady'));
                } else if (res.data.sourceReason === 'quota_exceeded') {
                    toast.info(t('courseIdeas.toast.quotaFallback', 'AI quota reached — showing curated evidence-based suggestions.'));
                } else {
                    toast.info(t('courseIdeas.toast.fallbackIdeas', 'Showing curated suggestions while AI is unavailable.'));
                }
                setViewMode('results');
                setShowAdvisor(false);
            } else {
                toast.error(t('courseIdeas.toast.generateError'));
            }
        } catch (error) {
            logger.error('Error generating ideas:', error);
            notifyFromApiError(error, {
                fallback: error.code === 'ECONNABORTED'
                    ? t('courseIdeas.toast.timeoutError')
                    : t('courseIdeas.toast.generalError'),
            });
        } finally {
            setIsGenerating(false);
        }
    };

    const handleAdvisorComplete = (profile) => {
        setLearningProfile(profile);
        generateIdeas({ learningProfile: profile, topic: profile.specificNeeds });
    };

    const saveIdea = async (idea) => {
        if (!userId) {
            toast.info(t('courseIdeas.toast.signInToSave'));
            navigate('/signin', { state: { returnTo: '/course-ideas' } });
            return;
        }
        try {
            const res = await axios.post(`${serverURL}/api/save-course-idea`, {
                userId,
                idea: { ...idea, generatedAt: new Date().toISOString() },
            }, { timeout: 30000 });
            if (res.data.success) {
                toast.success(t('courseIdeas.toast.ideaSaved'));
                trackInteraction(idea._id || idea.id, 'save');
                loadSavedIdeas(userId);
                loadTrendingIdeas();
            } else {
                toast.error(t('courseIdeas.toast.saveError'));
            }
        } catch (e) {
            notifyFromApiError(e, { fallback: t('courseIdeas.toast.saveError') });
        }
    };

    const deleteSavedIdea = async (ideaId) => {
        if (!ideaId) return;
        try {
            const res = await axios.delete(`${serverURL}/api/saved-course-ideas/${ideaId}`);
            if (res.data.success) {
                toast.success(t('courseIdeas.toast.ideaDeleted'));
                loadSavedIdeas(userId);
            }
        } catch (e) {
            notifyFromApiError(e, { fallback: t('courseIdeas.toast.deleteError') });
        }
    };

    const createCourse = (idea) => {
        trackInteraction(idea._id || idea.id, 'create');
        navigate('/genui', {
            state: {
                suggestedPrompt: idea.title,
                omitProjectRag: true,
            },
        });
    };

    const isAlreadySaved = (idea) => savedIdeas.some(
        (s) => (s._id || s.id) === (idea._id || idea.id) || s.title === idea.title
    );

    const filterAndSortIdeas = (ideas) => {
        let filtered = [...ideas];
        if (searchTerm) {
            const q = searchTerm.toLowerCase();
            filtered = filtered.filter((idea) =>
                idea.title?.toLowerCase().includes(q)
                || idea.description?.toLowerCase().includes(q)
                || idea.category?.toLowerCase().includes(q)
            );
        }
        if (filterDifficulty !== 'all') {
            filtered = filtered.filter((idea) =>
                (idea.difficulty || 'Beginner').toLowerCase() === filterDifficulty.toLowerCase()
            );
        }
        if (sortBy === 'duration') {
            filtered.sort((a, b) => {
                const aD = parseInt((a.estimatedDuration || a.duration || '').match(/\d+/)?.[0] || 0);
                const bD = parseInt((b.estimatedDuration || b.duration || '').match(/\d+/)?.[0] || 0);
                return aD - bD;
            });
        } else if (sortBy === 'difficulty') {
            const order = { Beginner: 1, Intermediate: 2, Advanced: 3, Mixed: 2 };
            filtered.sort((a, b) => (order[a.difficulty] || 1) - (order[b.difficulty] || 1));
        } else if (sortBy === 'trending') {
            filtered.sort((a, b) => (b.trendingScore || 0) - (a.trendingScore || 0));
        }
        return filtered;
    };

    const renderGrid = (ideas, opts = {}) => {
        const filtered = filterAndSortIdeas(ideas);
        if (filtered.length === 0) {
            return (
                <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                    <IoSearch className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-600 dark:text-gray-300">{t('courseIdeas.results.noResults')}</p>
                </div>
            );
        }
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {filtered.map((idea) => (
                    <div key={idea._id || idea.id || idea.title} onMouseEnter={() => trackInteraction(idea._id || idea.id, 'view')}>
                        <IdeaCard
                            idea={idea}
                            isSaved={opts.isSaved}
                            isAlreadySaved={isAlreadySaved(idea)}
                            showTrending={opts.showTrending}
                            isLoggedIn={Boolean(userId)}
                            onCreateCourse={createCourse}
                            onSave={saveIdea}
                            onDelete={deleteSavedIdea}
                        />
                    </div>
                ))}
            </div>
        );
    };

    const tabs = [
        { id: 'generate', label: t('courseIdeas.tabs.generate'), icon: IoBulb },
        { id: 'recent', label: t('courseIdeas.tabs.recent'), icon: IoTime },
        { id: 'trending', label: t('courseIdeas.tabs.trending'), icon: IoTrendingUp },
        ...(userId ? [{ id: 'saved', label: t('courseIdeas.tabs.saved'), icon: IoBookmark }] : []),
    ];

    // ─── Results view ───────────────────────────────────────────────────────
    if (viewMode === 'results' && generatedIdeas.length > 0) {
        const filtered = filterAndSortIdeas(generatedIdeas);
        return (
            <div className={embedded
                ? 'text-slate-900 dark:text-slate-100'
                : 'min-h-screen bg-gradient-to-b from-indigo-50/40 via-slate-50 to-slate-100 dark:from-[#080a0d] dark:via-[#0B0D12] dark:to-[#0B0D12]'
            }>
                {!embedded ? <Header /> : null}
                <div className={embedded ? '' : 'max-w-7xl mx-auto px-4 pt-4 sm:pt-6 pb-mobile-nav lg:pb-12'}>
                    {embedded ? (
                        <PageHeader
                            breadcrumb="Studio"
                            title={t('courseIdeas.results.title')}
                            subtitle={t('courseIdeas.results.subtitle', { count: filtered.length })}
                            actions={(
                                <button
                                    type="button"
                                    onClick={() => { setGeneratedIdeas([]); setViewMode('generate'); setSearchTerm(''); }}
                                    className="flex items-center gap-2 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl text-sm font-semibold transition-colors"
                                >
                                    <IoRefresh className="w-4 h-4" />
                                    {t('courseIdeas.actions.newSearch')}
                                </button>
                            )}
                        />
                    ) : (
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-white">
                                {t('courseIdeas.results.title')}
                            </h1>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                {t('courseIdeas.results.subtitle', { count: filtered.length })}
                                {lastSource === 'ai' && (
                                    <span className="ms-2 inline-flex items-center gap-1 text-blue-600 dark:text-blue-400">
                                        <IoSparkles className="w-3.5 h-3.5" /> AI
                                    </span>
                                )}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => { setGeneratedIdeas([]); setViewMode('generate'); setSearchTerm(''); }}
                            className="flex items-center gap-2 px-5 py-2.5 bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl text-sm font-semibold shadow-md shadow-indigo-500/20 transition-colors"
                        >
                            <IoRefresh className="w-4 h-4" />
                            {t('courseIdeas.actions.newSearch')}
                        </button>
                    </div>
                    )}

                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 mb-6 border border-gray-200 dark:border-gray-700">
                        <div className="flex flex-col sm:flex-row gap-3">
                            <input
                                type="text"
                                placeholder={t('courseIdeas.filters.search')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            />
                            <select value={filterDifficulty} onChange={(e) => setFilterDifficulty(e.target.value)}
                                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm">
                                <option value="all">{t('courseIdeas.filters.allDifficulties')}</option>
                                <option value="beginner">{t('courseIdeas.filters.beginner')}</option>
                                <option value="intermediate">{t('courseIdeas.filters.intermediate')}</option>
                                <option value="advanced">{t('courseIdeas.filters.advanced')}</option>
                            </select>
                            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm">
                                <option value="relevance">{t('courseIdeas.filters.relevance')}</option>
                                <option value="difficulty">{t('courseIdeas.filters.difficulty')}</option>
                                <option value="duration">{t('courseIdeas.filters.duration')}</option>
                            </select>
                        </div>
                    </div>

                    {renderGrid(generatedIdeas)}
                </div>
            </div>
        );
    }

    // ─── Main view ──────────────────────────────────────────────────────────
    return (
        <div className={embedded
            ? 'text-slate-900 dark:text-slate-100'
            : 'min-h-screen bg-gradient-to-b from-indigo-50/40 via-slate-50 to-slate-100 dark:from-[#080a0d] dark:via-[#0B0D12] dark:to-[#0B0D12]'
        }>
            {!embedded ? <Header /> : null}
            <div className={embedded ? '' : 'max-w-5xl mx-auto px-4 pt-4 sm:pt-6 pb-mobile-nav lg:pb-12'}>
                {embedded ? (
                    <PageHeader
                        breadcrumb="Studio"
                        title={t('courseIdeas.page.title')}
                        subtitle={t('courseIdeas.page.subtitle')}
                    />
                ) : (
                <div className="text-center mb-8">
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-100 dark:bg-indigo-950/40 rounded-full text-xs font-semibold text-indigo-700 dark:text-indigo-300 mb-4">
                        <IoSchool className="w-3.5 h-3.5" />
                        {t('courseIdeas.hero.badge')}
                    </motion.div>
                    <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 dark:text-white mb-3 tracking-tight">
                        {t('courseIdeas.page.title')}
                    </h1>
                    <p className="text-base text-slate-600 dark:text-slate-300 max-w-2xl mx-auto leading-relaxed">
                        {t('courseIdeas.page.subtitle')}
                    </p>
                </div>
                )}

                {projectCount > 0 && (
                    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                        className="mb-6 rounded-xl border border-indigo-200/70 dark:border-indigo-800/50 bg-indigo-50/60 dark:bg-indigo-950/20 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                            <span className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-950/50 flex items-center justify-center shrink-0">
                                <FaFolderOpen className="text-indigo-600 dark:text-indigo-400" size={16} />
                            </span>
                            <div className="min-w-0 text-start">
                                <p className="text-sm font-semibold text-slate-900 dark:text-white">{t('courseIdeas.projectsBanner.title')}</p>
                                <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">{t('courseIdeas.projectsBanner.body', { count: projectCount })}</p>
                            </div>
                        </div>
                        <button type="button" onClick={() => navigate('/create-choice', { state: { from: '/course-ideas' } })}
                            className="shrink-0 px-4 py-2.5 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold shadow-sm transition-colors">
                            {t('courseIdeas.projectsBanner.cta')}
                        </button>
                    </motion.div>
                )}

                {/* Tabs */}
                <div className="flex justify-center mb-8 overflow-x-auto">
                    <div className="inline-flex bg-white/90 dark:bg-slate-900/85 rounded-xl p-1 shadow-sm border border-slate-200/80 dark:border-slate-700/60">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            return (
                                <button
                                    key={tab.id}
                                    type="button"
                                    onClick={() => setViewMode(tab.id)}
                                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                                        viewMode === tab.id
                                            ? 'bg-indigo-500 text-white shadow-sm'
                                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-800/60'
                                    }`}
                                >
                                    <Icon className="w-4 h-4" />
                                    {tab.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Generate tab */}
                {viewMode === 'generate' && (
                    <div className="space-y-6">
                        {/* Mode cards */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <ModeCard
                                active={generateMode === 'quick'}
                                onClick={() => { setGenerateMode('quick'); setShowAdvisor(false); }}
                                icon={<IoBulb className="w-5 h-5" />}
                                title={t('courseIdeas.modes.quick')}
                                description={t('courseIdeas.modes.quickDesc')}
                            />
                            <ModeCard
                                active={generateMode === 'advisor'}
                                onClick={() => { setGenerateMode('advisor'); setShowAdvisor(true); }}
                                icon={<IoSparkles className="w-5 h-5" />}
                                title={t('courseIdeas.tabs.learningAdvisor')}
                                description={t('courseIdeas.modes.advisorDesc')}
                            />
                        </div>

                        {showAdvisor ? (
                            <LearningAdvisor
                                initialProfile={learningProfile || {}}
                                onComplete={handleAdvisorComplete}
                                onCancel={() => { setShowAdvisor(false); setGenerateMode('quick'); }}
                            />
                        ) : (
                            <div className="rounded-2xl border border-slate-200/80 dark:border-slate-700/70 bg-white/90 dark:bg-slate-900/85 shadow-sm p-6 sm:p-8">
                                <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-2">
                                    {t('courseIdeas.input.label')}
                                </label>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">{t('courseIdeas.input.hint')}</p>
                                <div className="flex flex-col sm:flex-row gap-3 mb-5">
                                    <input
                                        type="text"
                                        value={topic}
                                        onChange={(e) => setTopic(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && !isGenerating && generateIdeas()}
                                        placeholder={t('courseIdeas.input.placeholder')}
                                        disabled={isGenerating}
                                        className="flex-1 px-4 py-3 border border-slate-200 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-950/40 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 text-base"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => generateIdeas()}
                                        disabled={!topic.trim() || isGenerating}
                                        className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl font-semibold disabled:opacity-50 shadow-md shadow-indigo-500/20 transition-colors w-full sm:w-auto min-w-[10rem]"
                                    >
                                        {isGenerating ? (
                                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        ) : (
                                            <FaMagic className="w-4 h-4" />
                                        )}
                                        {isGenerating ? t('courseIdeas.actions.generating') : t('courseIdeas.actions.generate')}
                                    </button>
                                </div>

                                {/* Skill level */}
                                <div className="mb-5">
                                    <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-wide">
                                        {t('courseIdeas.skillLevel.label')}
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        {SKILL_LEVELS.map((level) => (
                                            <button
                                                key={level}
                                                type="button"
                                                onClick={() => setSkillLevel(level)}
                                                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                                                    skillLevel === level
                                                        ? 'bg-indigo-500 text-white shadow-sm'
                                                        : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                                                }`}
                                            >
                                                {level === 'all'
                                                    ? t('courseIdeas.skillLevel.all')
                                                    : t(`courseIdeas.filters.${level}`)}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Suggestions */}
                                {suggestions.length > 0 && (
                                    <div>
                                        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-wide">
                                            {t('courseIdeas.suggestedTopics')}
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            {suggestions.map((s) => (
                                                <button
                                                    key={s}
                                                    type="button"
                                                    onClick={() => setTopic(s)}
                                                    className="px-3 py-1.5 text-xs font-medium bg-indigo-50 dark:bg-indigo-950/30 text-indigo-800 dark:text-indigo-300 rounded-full border border-indigo-200/80 dark:border-indigo-800/50 hover:bg-indigo-100 dark:hover:bg-indigo-950/50 transition-colors"
                                                >
                                                    {s}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {isGenerating && (
                            <div className="text-center py-8 rounded-xl border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/60">
                                <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                                <p className="text-sm text-slate-500 dark:text-slate-400">{t('courseIdeas.toast.generatingIdeas')}</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Recent tab */}
                {viewMode === 'recent' && (
                    <div>
                        {loading.recent ? (
                            <div className="text-center py-16">
                                <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
                            </div>
                        ) : recentIdeas.length === 0 ? (
                            <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                                <IoTime className="w-14 h-14 text-gray-300 mx-auto mb-4" />
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                    {t('courseIdeas.recent.empty')}
                                </h3>
                                <p className="text-sm text-gray-500 mb-4">
                                    {userId
                                        ? t('courseIdeas.recent.emptyDesc')
                                        : t('courseIdeas.recent.guestDesc', 'Generate ideas while signed in to sync across devices, or browse locally after generating.')}
                                </p>
                                <button type="button" onClick={() => setViewMode('generate')}
                                    className="px-6 py-3 bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl text-sm font-semibold shadow-md shadow-indigo-500/20 transition-colors">
                                    {t('courseIdeas.actions.generate')}
                                </button>
                            </div>
                        ) : renderGrid(recentIdeas)}
                    </div>
                )}

                {/* Trending tab */}
                {viewMode === 'trending' && (
                    <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-6">
                            {t('courseIdeas.trending.desc', 'Popular ideas ranked by community engagement—saves, views, and course creations.')}
                        </p>
                        {loading.trending ? (
                            <div className="text-center py-16">
                                <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
                            </div>
                        ) : trendingIdeas.length === 0 ? (
                            <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                                <IoTrendingUp className="w-14 h-14 text-gray-300 mx-auto mb-4" />
                                <p className="text-gray-500">{t('courseIdeas.trending.empty')}</p>
                            </div>
                        ) : renderGrid(trendingIdeas, { showTrending: true })}
                    </div>
                )}

                {/* Saved tab */}
                {viewMode === 'saved' && userId && (
                    <div>
                        {loading.saved ? (
                            <div className="text-center py-16">
                                <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
                            </div>
                        ) : savedIdeas.length === 0 ? (
                            <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                                <IoBookmark className="w-14 h-14 text-gray-300 mx-auto mb-4" />
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                    {t('courseIdeas.saved.empty')}
                                </h3>
                                <p className="text-sm text-gray-500">{t('courseIdeas.saved.emptyDesc')}</p>
                            </div>
                        ) : renderGrid(savedIdeas, { isSaved: true })}
                    </div>
                )}
            </div>
        </div>
    );
};

function ModeCard({ active, onClick, icon, title, description }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`w-full text-start rounded-xl border-2 p-4 sm:p-5 transition-all active:scale-[0.99] ${
                active
                    ? 'border-indigo-400 bg-indigo-50/80 dark:bg-indigo-950/30 ring-2 ring-indigo-500/15 shadow-sm'
                    : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/60 hover:border-indigo-300 dark:hover:border-indigo-800'
            }`}
        >
            <div className="flex items-start gap-3">
                <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                    active ? 'bg-indigo-500 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'
                }`}>
                    {icon}
                </span>
                <div className="min-w-0">
                    <p className={`text-sm font-semibold ${active ? 'text-indigo-900 dark:text-indigo-100' : 'text-slate-900 dark:text-white'}`}>
                        {title}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">{description}</p>
                </div>
            </div>
        </button>
    );
}

export default CourseIdeas;
